import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getApiBaseUrl } from '@/lib/api';
import { toast } from 'sonner';
import axios from 'axios';
import { CheckCircle2, Loader2 } from 'lucide-react';

const DEFAULT_PLANS = {
  free: {
    name: 'Free',
    price: 0,
    analyses_limit: 3,
    exports_limit: 1,
    features: [
      '3 resume analyses per month',
      '1 PDF export per month',
      'Basic match scoring',
      'Skills gap analysis'
    ]
  },
  pro: {
    name: 'Pro',
    price: 19,
    analyses_limit: 50,
    exports_limit: -1,
    features: [
      '50 resume analyses per month',
      'Unlimited PDF exports',
      'Priority AI processing',
      'Detailed keyword analysis',
      'Section-by-section suggestions',
      'Email support'
    ]
  },
  business: {
    name: 'Business',
    price: 49,
    analyses_limit: 200,
    exports_limit: -1,
    features: [
      '200 resume analyses per month',
      'Unlimited PDF exports',
      'Priority AI processing',
      'Detailed keyword analysis',
      'Section-by-section suggestions',
      'Priority email support',
      'Team features (coming soon)'
    ]
  }
};

const API = getApiBaseUrl();

export default function PricingPage() {
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState(DEFAULT_PLANS);
  const [loading, setLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    const fetchPlans = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/plans`);
        const fetchedPlans = response?.data || {};
        const mergedPlans = {
          free: { ...DEFAULT_PLANS.free, ...(fetchedPlans.free || {}) },
          pro: { ...DEFAULT_PLANS.pro, ...(fetchedPlans.pro || {}) },
          business: { ...DEFAULT_PLANS.business, ...(fetchedPlans.business || {}) }
        };
        setPlans(mergedPlans);
      } catch (error) {
        console.error('Failed to fetch plans:', error);
        setPlans(DEFAULT_PLANS);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const handleUpgrade = async (planId) => {
    if (!isAuthenticated) {
      navigate('/signup');
      return;
    }

    if (planId === 'free') {
      toast.info('You are already on the free plan');
      return;
    }

    setCheckoutLoading(planId);

    try {
      const response = await axios.post(
        `${API}/billing/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      window.location.href = response.data.url;
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to start checkout';
      toast.error(message);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const planConfig = ['free', 'pro', 'business'];
  const planList = planConfig
    .map((id) => {
      const planData = plans[id] || DEFAULT_PLANS[id];
      if (!planData?.name) return null;
      return {
        id,
        ...planData,
        ...(id === 'pro' ? { popular: true } : {})
      };
    })
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Navbar />

      <main className="flex-1 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-slate-900 mb-4">
              Simple, transparent pricing
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Choose the plan that fits your job search. Start free, upgrade when you need more.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {planList.map((plan) => {
                const isCurrentPlan = user?.plan === plan.id;
                const isPro = plan.id === 'pro';

                return (
                  <Card 
                    key={plan.id}
                    className={`relative bg-white border ${isPro ? 'border-slate-900 ring-2 ring-slate-900' : 'border-slate-200'} shadow-[0_2px_8px_rgba(15,23,42,0.08)]`}
                  >
                    {isPro && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-slate-900 text-white">Most Popular</Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-2">
                      <CardTitle className="font-heading text-xl">{plan.name}</CardTitle>
                      <div className="mt-4">
                        <span className="font-heading font-extrabold text-4xl text-slate-900">
                          ${plan.price || 0}
                        </span>
                        {(plan.price || 0) > 0 && (
                          <span className="text-slate-500">/month</span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <ul className="space-y-3">
                        {(plan.features || []).map((feature, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm">
                            <CheckCircle2 className="w-5 h-5 text-lime-500 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-700">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <Button
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={isCurrentPlan || checkoutLoading === plan.id}
                        className={`w-full ${isPro 
                          ? 'bg-slate-900 text-white hover:bg-slate-800' 
                          : 'bg-white border border-slate-200 text-slate-900 hover:bg-slate-50'
                        }`}
                        data-testid={`plan-${plan.id}-btn`}
                      >
                        {checkoutLoading === plan.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : isCurrentPlan ? (
                          'Current Plan'
                        ) : plan.id === 'free' ? (
                          'Get Started'
                        ) : (
                          'Upgrade'
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <div className="text-center mt-12">
            <p className="text-slate-600">
              All plans include SSL encryption and secure data handling.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
